import classNames from 'classnames';
import { isWeekend } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { TwTheme } from '../../../Tw';
import { getBestContrastColor } from '../../common/color.helper';
import useFormatter from '../../common/useFormatter';
import { TooltipItem } from '../../components/stats/TooltipItem';
import { formatChartAxisLabel, parseChartDate } from './chartUtils';

const colors = TwTheme.extend.colors;
const theme = TwTheme.extend.colors.chart;

type ActualityAggregatedStatsBarChartTooltipProps = {
  datum: {
    id: string | number;
    data: any;
    color: string;
    formattedValue: string;
  };
  totalX?: number;
};

const ActualityAggregatedStatsBarChartTooltip: React.FC<
  ActualityAggregatedStatsBarChartTooltipProps
> = props => {
  const { t } = useTranslation('feed');
  const formatter = useFormatter();
  const data = props.datum.data;
  const rawDate = data.index;

  // Use shared date parsing
  const { date, isValidDate } = parseChartDate(rawDate);
  const weekend = !isValidDate ? false : isWeekend(date);

  const dayTitleShort = !isValidDate
    ? String(rawDate || t`No Data`)
    : formatChartAxisLabel(date, 'EE dd MMM HH:mm');
  const weekdayTitle = !isValidDate ? null : formatChartAxisLabel(date, 'EE');

  return (
    <div
      key={props.datum.id}
      data-component="ActualityAggregatedStatsBarChartTooltip"
      className={classNames(
        'bg-app-panel-dark/90',
        'backdrop-blur backdrop-filter',
        'text-sm',
        'whitespace-nowrap',
      )}
    >
      <div className="min-w-tooltip">
        <div
          className={classNames(
            'flex items-center p-2 text-xs ltr:pl-3 rtl:pr-3',
          )}
          style={{
            backgroundColor: props.datum.color,
            color: getBestContrastColor(props.datum.color),
          }}
        >
          {props.datum.id}
        </div>
        <div
          className={classNames(
            'flex items-center',
            'px-2 py-2',
            'text-start text-sm font-bold',
            'bg-app-panel-dark',
          )}
        >
          <span className={classNames('ltr:pl-1 rtl:pr-1', 'flex-1')}>
            {dayTitleShort}
          </span>
          {weekdayTitle && (
            <span
              className={classNames(
                'ltr:ml-3 rtl:mr-3',
                'px-2 py-0.5',
                'rounded uppercase',
                'border',
                weekend ? 'border-menu/50 text-menu-active' : 'border-menu',
              )}
            >
              {weekdayTitle}
            </span>
          )}
        </div>
      </div>

      <div
        className={classNames(
          'px-1',
          'divide-menu/50 divide-y divide-solid divide-opacity-50',
          'border-menu/75 border-t',
        )}
      >
        <TooltipItem
          classNameLabel="bg-menu/20 text-menu-text/80"
          classNameValue="bg-transparent"
          title={props.datum.data.index}
          value={props.datum.formattedValue}
        />
      </div>
    </div>
  );
};

export default ActualityAggregatedStatsBarChartTooltip;
